/**
 * Portal week grid: 7 AM–4 PM (compact), ±15 min leeway; merge gaps ≤15 min.
 */

import { parseTimeToMinutes, toDateKey } from './dayScheduleUtils';

/** Pixels per hour on the portal week grid (time column + day columns stay in sync) */
export const WEEK_HOUR_PX = 52;
/** First row label (excludes 6 AM) */
export const WEEK_START_H = 7;
/** Exclusive end hour — rows are 7 … 16 (7 AM … 4 PM); 5 PM / 6 PM not shown */
export const WEEK_END_H = 17;
export const WEEK_SCHED_HRS = Array.from({ length: WEEK_END_H - WEEK_START_H }, (_, i) => i + WEEK_START_H);

/** Minutes padded before/after each booking for blocking + merging with nearby work */
export const SCHEDULE_LEEWAY_MIN = 15;
/** If gap between end of one job and start of next ≤ this, show as one block (never merges two inquiries) */
export const SCHEDULE_MERGE_GAP_MIN = 15;

/** Fixed duration used for portal inquiries in the grid and overlap checks (minutes) */
export const PORTAL_INQUIRY_DURATION_MIN = 30;

/**
 * Only these inquiry statuses may drag-reschedule from the portal (before staff has confirmed).
 * Staff confirmation sets status to `new` — that must stay locked here (matches PATCH).
 * Won, quoted, inspection*, etc. stay locked even if job_id is missing in the payload.
 */
const PORTAL_DRAG_ALLOWED_STATUSES = new Set(['portal_review', 'pending', 'draft']);

/** Terminal / lost — never draggable from portal */
const PORTAL_RESCHEDULE_BLOCKED_STATUSES = new Set(['won', 'lost']);

/**
 * True if a 30-min inquiry window at (dateStr, timeStr) overlaps another portal inquiry on the same day.
 * @param {string|null|undefined} excludeInquiryId — when dragging/rescheduling, exclude this id
 */
export function portalInquiryOverlapsSibling(inquiries, dateStr, timeStr, excludeInquiryId = null) {
  const sm = parseTimeToMinutes(timeStr);
  if (sm === null || sm === undefined) return false;
  const myStart = sm;
  const myEnd = sm + PORTAL_INQUIRY_DURATION_MIN;

  for (const inq of inquiries || []) {
    if (excludeInquiryId != null && String(inq.id) === String(excludeInquiryId)) continue;
    const dk = inquiryDateKey(inq);
    if (!dk || dk !== dateStr) continue;
    const osm = parseTimeToMinutes(inq.preferred_time);
    if (osm === null || osm === undefined) continue;
    const oStart = osm;
    const oEnd = osm + PORTAL_INQUIRY_DURATION_MIN;
    if (myStart < oEnd && oStart < myEnd) return true;
  }
  return false;
}

export function inquiryAllowsPortalDrag(inq) {
  if (!inq) return false;
  if (inq.job_id) return false;
  if (inq.quote_id) return false;
  if (inq.inspection_id) return false;
  const s = String(inq.status ?? '').toLowerCase().trim();
  if (PORTAL_RESCHEDULE_BLOCKED_STATUSES.has(s)) return false;
  if (!s) return true;
  return PORTAL_DRAG_ALLOWED_STATUSES.has(s);
}

export const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const timeToPx = (t, hourPx = WEEK_HOUR_PX) => {
  if (!t) return 0;
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return 0;
  return ((h - WEEK_START_H) + (Number.isNaN(m) ? 0 : m) / 60) * hourPx;
};

export const pxToTime = (px, hourPx = WEEK_HOUR_PX) => {
  const totalMins = Math.round((px / hourPx) * (60 / 15)) * 15;
  const rawH = Math.floor(totalMins / 60) + WEEK_START_H;
  const h = Math.max(WEEK_START_H, Math.min(WEEK_END_H - 1, rawH));
  const m = totalMins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const fmtHour = (h) => (h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`);

export function buildWeekDates(weekOffset = 0) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today);
  start.setDate(today.getDate() + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function jobDurationMinutes(job) {
  const eh = Number(job.estimated_hours);
  if (Number.isFinite(eh) && eh > 0) return Math.round(eh * 60);
  return 60;
}

function inquiryDateKey(inq) {
  return toDateKey(inq.preferred_date || inq.created_at);
}

function buildTitleForGroup(variant, items) {
  if (variant === 'blocked') {
    const n = items.length;
    return n > 1 ? `Busy · ${n} bookings` : 'Busy';
  }
  if (variant === 'yours_jobs') {
    const types = items
      .filter((i) => i.kind === 'job')
      .map((i) => i.job?.job_type || 'Job');
    const uniq = [...new Set(types)];
    if (uniq.length === 1) return uniq[0];
    if (uniq.length <= 2) return uniq.join(' · ');
    return `${uniq[0]} +${uniq.length - 1} more`;
  }
  if (variant === 'yours_inquiry') {
    const inq = items.find((i) => i.kind === 'inquiry')?.inquiry;
    const t = inq?.issue || 'Service request';
    return t.length > 40 ? `${t.slice(0, 37)}…` : t;
  }
  return 'Your bookings';
}

function classifyMergedGroup(items) {
  const hasOther = items.some((i) => i.kind === 'job' && !i.isYours);
  if (hasOther) return 'blocked';
  const hasInq = items.some((i) => i.kind === 'inquiry');
  const hasJob = items.some((i) => i.kind === 'job');
  if (hasInq && !hasJob) return 'yours_inquiry';
  if (hasJob && !hasInq) return 'yours_jobs';
  return 'yours_mixed';
}

/**
 * @param {Array} taggedJobs — jobs with `is_yours` boolean
 * @param {Array} inquiries — portal service requests for this customer
 * @param {Set<string>} dateStrSet
 * @returns {{ byDate: Record<string, Array>, noTime: Array, blockedRangesByDate: Record<string, Array<{startMin: number, endMin: number}>> }}
 */
export function buildWeekScheduleSegments(taggedJobs, inquiries, dateStrSet, hourPx = WEEK_HOUR_PX) {
  const byDate = {};
  const blockedRangesByDate = {};
  dateStrSet.forEach((ds) => {
    byDate[ds] = [];
    blockedRangesByDate[ds] = [];
  });
  const noTime = [];

  const gridStartMin = WEEK_START_H * 60;
  const gridEndMin = WEEK_END_H * 60;

  const rawByDate = {};
  dateStrSet.forEach((ds) => {
    rawByDate[ds] = [];
  });

  (taggedJobs || []).forEach((job) => {
    const dk = toDateKey(job.scheduled_date);
    if (!dk || !dateStrSet.has(dk)) return;
    const sm = parseTimeToMinutes(job.scheduled_time);
    if (sm === null || sm === undefined) {
      noTime.push({ kind: 'job', job });
      return;
    }
    const em = sm + jobDurationMinutes(job);
    rawByDate[dk].push({
      start: sm,
      end: em,
      kind: 'job',
      isYours: !!job.is_yours,
      job
    });
  });

  (inquiries || []).forEach((inq) => {
    const dk = inquiryDateKey(inq);
    if (!dk || !dateStrSet.has(dk)) return;
    const sm = parseTimeToMinutes(inq.preferred_time);
    if (sm === null || sm === undefined) {
      noTime.push({ kind: 'inquiry', inquiry: inq });
      return;
    }
    const em = sm + PORTAL_INQUIRY_DURATION_MIN;
    rawByDate[dk].push({
      start: sm,
      end: em,
      kind: 'inquiry',
      isYours: true,
      inquiry: inq
    });
  });

  Object.keys(rawByDate).forEach((dk) => {
    const raw = rawByDate[dk];
    if (!raw.length) return;

    raw.sort((a, b) => a.start - b.start);

    const groups = [];
    for (const r of raw) {
      if (!groups.length) {
        groups.push({ start: r.start, end: r.end, items: [r] });
        continue;
      }
      const last = groups[groups.length - 1];
      const gapOk = r.start - last.end <= SCHEDULE_MERGE_GAP_MIN;
      const lastHasInquiry = last.items.some((i) => i.kind === 'inquiry');
      const rIsInquiry = r.kind === 'inquiry';
      /** Never merge two portal inquiries into one card — each stays its own block */
      const wouldMergeTwoInquiries = lastHasInquiry && rIsInquiry;
      if (gapOk && !wouldMergeTwoInquiries) {
        last.end = Math.max(last.end, r.end);
        last.items.push(r);
      } else {
        groups.push({ start: r.start, end: r.end, items: [r] });
      }
    }

    groups.forEach((g, gi) => {
      const variant = classifyMergedGroup(g.items);
      const dispStart = Math.max(0, g.start - SCHEDULE_LEEWAY_MIN);
      const dispEnd = g.end + SCHEDULE_LEEWAY_MIN;
      const clipStart = Math.max(gridStartMin, dispStart);
      const clipEnd = Math.min(gridEndMin, dispEnd);
      if (clipEnd <= clipStart) return;

      blockedRangesByDate[dk].push({ startMin: clipStart, endMin: clipEnd });

      // Visual box: exact booking window (clipped), same math as timeToPx — aligns with hour lines.
      // Leeway is not applied to top/height so cards sit on the horizontal grid lines.
      const visStart = Math.max(gridStartMin, g.start);
      const visEnd = Math.min(gridEndMin, g.end);
      if (visEnd <= visStart) return;

      const top = ((visStart - gridStartMin) / 60) * hourPx;
      const height = Math.max(22, ((visEnd - visStart) / 60) * hourPx);

      const title = buildTitleForGroup(variant, g.items);
      const subtitle =
        variant === 'blocked'
          ? null
          : variant === 'yours_mixed'
            ? 'Jobs & requests'
            : variant === 'yours_jobs' && g.items.length > 1
              ? `${g.items.filter((i) => i.kind === 'job').length} visits`
              : null;

      const key = `seg-${dk}-${gi}-${g.start}`;
      const soleInquiry =
        variant === 'yours_inquiry' && g.items.length === 1 && g.items[0].kind === 'inquiry'
          ? g.items[0].inquiry
          : null;
      const draggableInquiryId = soleInquiry && inquiryAllowsPortalDrag(soleInquiry) ? soleInquiry.id : null;
      const inquiryLocked = !!soleInquiry && !draggableInquiryId;

      byDate[dk].push({
        key,
        variant,
        top,
        height,
        title,
        subtitle,
        mergedCount: g.items.length,
        draggableInquiryId,
        inquiryLocked
      });
    });
  });

  return { byDate, noTime, blockedRangesByDate };
}

export function isMinuteBlocked(dateStr, timeStr, blockedRangesByDate) {
  const m = parseTimeToMinutes(timeStr);
  if (m === null || m === undefined) return false;
  const ranges = blockedRangesByDate[dateStr] || [];
  return ranges.some(({ startMin, endMin }) => m >= startMin && m < endMin);
}
